import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ensureUserGraphemeMeaningCard } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.create";

/**
 * Put a grapheme in the learn queue instead of enrolling it, because the free learner has used up
 * today's new-grapheme allowance.
 *
 * Like "skip until tomorrow", this writes the non-study `user_card` shape (no FSRS card, no
 * `startedStudyingAt`), so a queued character is not studied, never becomes due, and does not count
 * towards the day's allowance. Only `queuedAt` is written, and only the FIRST time: re-queuing keeps
 * the card's place in line. Enrollment (`createUserCard`) clears it.
 *
 * A grapheme that is already studied is left alone and reported as not queued.
 */
export async function queueGraphemeForLearning({
  actorDb,
  grapheme,
  now,
}: {
  actorDb: ActorDatabase;
  grapheme: string;
  now: Date;
}): Promise<{ isQueued: boolean }> {
  const userCard = await ensureUserGraphemeMeaningCard(actorDb, grapheme);
  if (userCard.fsrsCardId !== null) {
    return { isQueued: false };
  }
  if (userCard.queuedAt !== null) {
    return { isQueued: true };
  }
  await actorDb(import.meta.location)
    .update(actorSchema.userCard)
    .set({ deletedAt: null, queuedAt: now.toISOString() })
    .where(eq(actorSchema.userCard.id, userCard.id))
    .run();
  return { isQueued: true };
}
